import { useState, type ReactNode } from 'react';
import type { OrgUnitKind } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import {
  useCreateDepartment,
  useCreateTeam,
  useCreateVenture,
  useDepartments,
  useVentures,
} from '../api/organisation-api';
import styles from './NewUnitDialog.module.css';

/**
 * F3.01 — add a venture, a department or a team.
 *
 * One dialog for all three levels rather than three screens, because they differ by
 * two fields and a parent picker. The level is chosen first, so the form under it
 * only ever asks for what that level needs.
 *
 * A department's code is required and explained: it becomes half of every position
 * code beneath it (`NF-FIN-014`), so it is not the throwaway field it looks like.
 */

const KIND_OPTIONS = [
  { value: 'venture', label: 'Venture' },
  { value: 'department', label: 'Department' },
  { value: 'team', label: 'Team' },
];

export function NewUnitDialog({
  onClose,
  initialKind = 'department',
}: {
  readonly onClose: () => void;
  /** The first unit of an empty tree can only be a venture. */
  readonly initialKind?: OrgUnitKind;
}): ReactNode {
  const showToast = useToasts((state) => state.show);
  const ventures = useVentures();
  const departments = useDepartments();

  const createVenture = useCreateVenture();
  const createDepartment = useCreateDepartment();
  const createTeam = useCreateTeam();

  const [kind, setKind] = useState<OrgUnitKind>(initialKind);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isPending = createVenture.isPending || createDepartment.isPending || createTeam.isPending;
  const needsCode = kind !== 'team';
  const needsParent = kind !== 'venture';

  async function submit(): Promise<void> {
    setError(null);
    try {
      if (kind === 'venture') {
        await createVenture.mutateAsync({
          name: name.trim(),
          code: code.trim().toUpperCase(),
          defaultCurrency: 'BDT',
          timezone: 'Asia/Dhaka',
        });
      } else if (kind === 'department') {
        if (!parentId) return setError('Choose the venture this department sits in.');
        await createDepartment.mutateAsync({
          ventureId: parentId,
          name: name.trim(),
          code: code.trim().toUpperCase(),
        });
      } else {
        if (!parentId) return setError('Choose the department this team sits in.');
        await createTeam.mutateAsync({ departmentId: parentId, name: name.trim() });
      }

      showToast({ tone: 'success', message: `${name.trim()} added` });
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  const parentOptions =
    kind === 'department'
      ? (ventures.data ?? []).map((v) => ({ value: v.id, label: v.name, meta: v.code }))
      : (departments.data ?? []).map((d) => ({
          value: d.id,
          label: d.name,
          meta: d.code ?? undefined,
        }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Add a unit"
      description="Ventures hold departments, and departments hold teams. Seats hang off a department."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            isPending={isPending}
            disabled={name.trim() === '' || (needsCode && code.trim() === '')}
            onClick={() => void submit()}
          >
            Add it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Level">
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Level"
              options={KIND_OPTIONS}
              value={kind}
              onChange={(value) => {
                setKind(value as OrgUnitKind);
                setParentId(null);
              }}
            />
          )}
        </Field>

        {needsParent && (
          <Field label={kind === 'department' ? 'Venture' : 'Department'} isRequired>
            {({ inputId }) => (
              <Select
                id={inputId}
                label={kind === 'department' ? 'Venture' : 'Department'}
                placeholder={kind === 'department' ? 'Choose a venture' : 'Choose a department'}
                options={parentOptions}
                value={parentId}
                onChange={setParentId}
              />
            )}
          </Field>
        )}

        <Field label="Name" isRequired>
          {({ inputId, isInvalid }) => (
            <Input
              id={inputId}
              isInvalid={isInvalid}
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              placeholder={kind === 'team' ? 'Finance core' : 'Finance'}
            />
          )}
        </Field>

        {needsCode && (
          <Field
            label="Code"
            isRequired
            hint="Two to twelve letters or numbers. It becomes part of every position code underneath this unit — NF-FIN-014."
          >
            {({ inputId, isInvalid }) => (
              <Input
                id={inputId}
                isInvalid={isInvalid}
                value={code}
                isMono
                maxLength={12}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder={kind === 'venture' ? 'NF' : 'FIN'}
              />
            )}
          </Field>
        )}

        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
